// The Incidents list's "More filters": who raised an incident, who holds it, the
// two deployments, the Medical detail type and sub-type, and a search of what
// was written on it.
//
// All of it runs over the rows the list already fetched — GET /incidents/ serves
// each incident whole, notes included — so none of these filters costs a request,
// and none of them can reveal anything: a Race Organiser's rows arrive with the
// treatment and the drug notes already stripped (core/clinical.py), so searching
// them finds only what that reader was sent.
//
// DOM-free on purpose, like incidentMap.js, so the rules can be pinned down by a
// spec without rendering the page (see incidentFilters.test.js).
import { plainText } from "./richText";

const lower = (s) => (s || "").trim().toLowerCase();

// --- People: created by / allocated to ----------------------------------------
// The snapshots on an incident may carry an email, or only a name (older rows,
// and anyone typed in by hand). One person is therefore matched the way the
// page's "Show" scope matches the reader: by email when both sides have one,
// otherwise by name.

const personOption = (email, name) => ({
  key: lower(email) ? `e:${lower(email)}` : `n:${lower(name)}`,
  email: lower(email), name: (name || "").trim(),
});

/** Whether a snapshot (email, name) is the person an option stands for. */
export const personMatches = (opt, email, name) => {
  if (!opt) return true;
  if (opt.team != null) return false;
  const e = lower(email);
  if (opt.email && e) return e === opt.email;
  return !!lower(name) && lower(name) === lower(opt.name);
};

// Distinct people (and, for allocation, teams) across the rows, each with how
// many incidents it covers, most first then by name. A name-only snapshot is
// folded into the emailed person of the same name rather than listed twice.
const collect = (rows, pick) => {
  const byKey = new Map();
  const add = (opt) => {
    const hit = byKey.get(opt.key);
    if (hit) hit.count += 1; else byKey.set(opt.key, { ...opt, count: 1 });
  };
  const nameOnly = [];
  rows.forEach(i => {
    const opt = pick(i);
    if (!opt) return;
    if (opt.team == null && !opt.email) nameOnly.push(opt); else add(opt);
  });
  const emailed = [...byKey.values()].filter(o => o.email);
  nameOnly.forEach(opt => {
    const same = emailed.find(o => lower(o.name) === lower(opt.name));
    if (same) same.count += 1; else add(opt);
  });
  return [...byKey.values()].sort(
    (a, b) => b.count - a.count || a.label.localeCompare(b.label));
};

/** Everyone who raised one of these incidents, for the "Created by" picker. */
export const creatorOptions = (rows) => collect(rows, (i) => {
  if (!lower(i.created_by_email) && !lower(i.created_by_name)) return null;
  const opt = personOption(i.created_by_email, i.created_by_name);
  return { ...opt, label: opt.name || opt.email };
});

/** Everyone, and every team, holding one of these incidents. */
export const allocationOptions = (rows) => collect(rows, (i) => {
  if (i.allocated_to_team_id != null) {
    return { key: `t:${i.allocated_to_team_id}`, team: i.allocated_to_team_id,
             label: `${i.allocated_to_name || "Team"} (team)` };
  }
  if (!lower(i.allocated_to_email) && !lower(i.allocated_to_name)) return null;
  const opt = personOption(i.allocated_to_email, i.allocated_to_name);
  return { ...opt, label: opt.name || opt.email };
});

export const createdByMatches = (opt, i) =>
  !opt || personMatches(opt, i.created_by_email, i.created_by_name);

// A team allocation is matched on the team, never on the name it snapshots, so a
// person who happens to share a team's name can't be confused with it.
export const allocatedToMatches = (opt, i) => {
  if (!opt) return true;
  if (opt.team != null) return i.allocated_to_team_id === opt.team;
  return i.allocated_to_team_id == null
    && personMatches(opt, i.allocated_to_email, i.allocated_to_name);
};

// --- One-of-a-list fields: deployments, Medical detail type and sub-type ---------
// "" is not filtering; the two sentinels ask whether anything was recorded at all,
// and any other value is one entry from the field's option list.
export const CHOICE_ANY = "__any__";
export const CHOICE_NONE = "__none__";

export const choiceMatches = (want, value) => {
  if (!want) return true;
  const v = (value || "").trim();
  if (want === CHOICE_ANY) return !!v;
  if (want === CHOICE_NONE) return !v;
  return v === want;
};

/**
 * The sub-types to offer once a detail type is chosen: that type's own list, then
 * anything else these rows recorded under it — older rows hold values the list no
 * longer offers ("Pain Management", "Taping"). A value nobody can pick
 * would be a row the filter could never reach.
 */
export const subtypeOptions = (rows, detailType, listed = []) => {
  const seen = new Set(listed);
  const extra = new Set();
  rows.forEach(i => {
    const v = (i.detail_subtype || "").trim();
    if ((i.detail_type || "").trim() === detailType && v && !seen.has(v)) extra.add(v);
  });
  return [...listed, ...[...extra].sort((a, b) => a.localeCompare(b))];
};

// --- Searching ----------------------------------------------------------------

/** The main search box: the patient's name or race number, nothing else. */
export const patientMatches = (i, query) => {
  const q = lower(query);
  return !q || `${i.patient_name || ""} ${i.patient_race_number || ""}`.toLowerCase().includes(q);
};

/**
 * What an incident says, as one lower-cased string: summary, the description of
 * an Other sub-type, details, treatment given, consent details and every note. The details and notes are rich text, so they are read as
 * a person reads them (plainText) — a photo's URL or a formatting mark must not
 * be what a search matched. Built once per load (see the page), not per keystroke.
 */
export const recordText = (i) => [
  i.incident_summary,
  i.detail_other,  // what an "Other" sub-type was, in the recorder's words
  plainText(i.incident_details, { photos: "drop" }),
  i.treatment_given,
  i.patient_consent_details,
  ...(i.notes || []).map(n => plainText(n.details, { photos: "drop" })),
].filter(Boolean).join("\n").toLowerCase();

/** The words of a record search; every one must appear, in any order. */
export const searchTerms = (query) => lower(query).split(/\s+/).filter(Boolean);

export const recordMatches = (text, terms) => terms.every(t => text.includes(t));
